import {
  type ChangeEvent, type ReactElement, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import EmptyList from 'components/EmptyList/EmptyList';
import Panel from 'components/Panel/Panel';

import { formatIsoDateTime } from './groupWatchRuns';
import { issueTrendBySeverity } from './runAnalysisSelectors';
import { MONITORING_AGENT_TYPES } from './scheduleWatchAgents';
import WatchTowerFoldPanel from './WatchTowerFoldPanel';
import WatchTowerIssueOverview from './WatchTowerIssueOverview';
import WatchTowerIssueTrend from './WatchTowerIssueTrend';
import { formatAgentType } from './watchTowerSelectors';

import type {
  IssueMapMarker,
  IssueTrendGrain,
  RunAnalysisKpis,
  RunAnalysisSpanDays,
  RunIssueCard,
  TimelineRange,
} from './runAnalysisSelectors';

import styles from './scss/TargetedMarketing.module.scss';

const RUN_STATUSES = ['completed', 'failed', 'running'];

interface IWatchTowerRunAnalysis {
  windowDays: RunAnalysisSpanDays,
  agentType: string,
  country: string,
  severity: string,
  runStatus: string,
  countries: string[],
  severities: string[],
  kpis: RunAnalysisKpis,
  markers: IssueMapMarker[],
  timelineDays: string[],
  timelineRange: TimelineRange,
  markCounts: Record<string, number>,
  severityRows: string[],
  severityAgentCounts: Record<string, number>,
  rangeIssues: RunIssueCard[],
  issues: RunIssueCard[],
  onWindowChange: (days: RunAnalysisSpanDays) => void,
  onAgentChange: (type: string) => void,
  onCountryChange: (country: string) => void,
  onSeverityChange: (severity: string) => void,
  onRunStatusChange: (status: string) => void,
  onMapCountryClick: (country: string) => void,
  onTimelineRangeChange: (range: TimelineRange) => void,
  onTimelineMarkClick: (day: string) => void,
  onSeverityAgentClick: (severity: string, agentType: string) => void,
  onOpenIssue: (card: RunIssueCard) => void,
}

function severityClass(severity: string): string {
  if (severity === 'high' || severity === 'critical') {
    return styles.severityError;
  }
  if (severity === 'medium') {
    return styles.severityWarning;
  }
  return styles.muted;
}

function statusClass(status: string): string {
  if (status === 'completed') {
    return styles.statusActive;
  }
  if (status === 'failed') {
    return styles.statusFailed;
  }
  return styles.statusDraft;
}

function WatchTowerRunAnalysis({
  windowDays,
  agentType,
  country,
  severity,
  runStatus,
  countries,
  severities,
  kpis,
  markers,
  timelineDays,
  timelineRange,
  markCounts,
  severityRows,
  severityAgentCounts,
  rangeIssues,
  issues,
  onWindowChange,
  onAgentChange,
  onCountryChange,
  onSeverityChange,
  onRunStatusChange,
  onMapCountryClick,
  onTimelineRangeChange,
  onTimelineMarkClick,
  onSeverityAgentClick,
  onOpenIssue,
}: IWatchTowerRunAnalysis): ReactElement {
  const { t } = useTranslation();
  const [trendOpen, setTrendOpen] = useState(true);
  const [trendGrain, setTrendGrain] = useState<IssueTrendGrain>('day');

  const onWindowSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    onWindowChange(Number(event.target.value) as RunAnalysisSpanDays);
  };

  const onTrendGrainSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setTrendGrain(event.target.value as IssueTrendGrain);
  };

  const startDay = timelineDays[timelineRange[0]] || '';
  const endDay = timelineDays[timelineRange[1]] || '';
  const trendSeries = useMemo(
    () => issueTrendBySeverity(rangeIssues, startDay, endDay, trendGrain),
    [endDay, rangeIssues, startDay, trendGrain],
  );

  return (
    <Panel className={styles.panel}>
      <div className={styles.toolbar}>
        <h2 className={styles.sectionTitle}>{t('targetedMarketing.runAnalysisTitle')}</h2>
        <select
          className={styles.filterNative}
          aria-label={t('targetedMarketing.pulseWindow')}
          value={windowDays}
          onChange={onWindowSelect}
        >
          <option value={7}>{t('targetedMarketing.window7')}</option>
          <option value={30}>{t('targetedMarketing.window30')}</option>
          <option value={90}>{t('targetedMarketing.window90')}</option>
        </select>
      </div>
      <p className={styles.muted}>{t('targetedMarketing.runAnalysisHint')}</p>
      <div className={styles.kpiRow}>
        <article className={styles.kpiCard}>
          <p className={styles.kpiLabel}>{t('targetedMarketing.kpiRuns')}</p>
          <p className={styles.kpiValue}>{kpis.runCount}</p>
        </article>
        <article className={styles.kpiCard}>
          <p className={styles.kpiLabel}>{t('targetedMarketing.kpiFailedRuns')}</p>
          <p className={styles.kpiValue}>{kpis.failedCount}</p>
        </article>
        <article className={styles.kpiCard}>
          <p className={styles.kpiLabel}>{t('targetedMarketing.kpiRunIssues')}</p>
          <p className={styles.kpiValue}>{kpis.issueCount}</p>
        </article>
        <article className={styles.kpiCard}>
          <p className={styles.kpiLabel}>{t('targetedMarketing.kpiAgentMix')}</p>
          {kpis.agentMix.length === 0 ? (
            <p className={styles.muted}>{t('targetedMarketing.none')}</p>
          ) : (
            <ul className={styles.kpiMix}>
              {kpis.agentMix.map((item) => (
                <li key={item.agentType}>
                  {formatAgentType(item.agentType)}
                  {': '}
                  {item.count}
                </li>
              ))}
            </ul>
          )}
        </article>
      </div>

      <WatchTowerIssueOverview
        agentType={agentType}
        country={country}
        severity={severity}
        markers={markers}
        timelineDays={timelineDays}
        timelineRange={timelineRange}
        markCounts={markCounts}
        severityRows={severityRows}
        severityAgentCounts={severityAgentCounts}
        afterTimeline={(
          <WatchTowerFoldPanel
            id="run-analysis-issue-trend"
            title={t('targetedMarketing.issueTrendTitle')}
            open={trendOpen}
            onToggle={() => setTrendOpen((open) => !open)}
          >
            <div className={styles.foldToolbar}>
              <p className={styles.muted}>{t('targetedMarketing.issueTrendHint')}</p>
              <select
                className={styles.filterNative}
                aria-label={t('targetedMarketing.issueTrendGrain')}
                value={trendGrain}
                onChange={onTrendGrainSelect}
              >
                <option value="day">{t('targetedMarketing.issueTrendGrainDay')}</option>
                <option value="week">{t('targetedMarketing.issueTrendGrainWeek')}</option>
                <option value="month">{t('targetedMarketing.issueTrendGrainMonth')}</option>
              </select>
            </div>
            {trendSeries.buckets.length === 0 ? (
              <EmptyList text={t('targetedMarketing.emptyIssueTrend')} />
            ) : (
              <WatchTowerIssueTrend series={trendSeries} />
            )}
          </WatchTowerFoldPanel>
        )}
        emptyHeatText={t('targetedMarketing.emptyRunAnalysis')}
        mapPanelId="run-analysis-issue-map"
        heatPanelId="run-analysis-heat"
        onMapCountryClick={onMapCountryClick}
        onTimelineRangeChange={onTimelineRangeChange}
        onTimelineMarkClick={onTimelineMarkClick}
        onSeverityAgentClick={onSeverityAgentClick}
        onOpenIssue={onOpenIssue}
      />

      <h3 className={styles.sectionTitle}>{t('targetedMarketing.runIssuesTitle')}</h3>
      <div className={styles.filters}>
        <select
          className={styles.filterNative}
          aria-label={t('targetedMarketing.selectAgentType')}
          value={agentType}
          onChange={(event) => onAgentChange(event.target.value)}
        >
          <option value="">{t('targetedMarketing.allAgents')}</option>
          {MONITORING_AGENT_TYPES.map((item) => (
            <option key={item.type} value={item.type}>{item.label}</option>
          ))}
        </select>
        <select
          className={styles.filterNative}
          aria-label={t('targetedMarketing.country')}
          value={country}
          onChange={(event) => onCountryChange(event.target.value)}
        >
          <option value="">{t('targetedMarketing.allCountries')}</option>
          {countries.map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
        <select
          className={styles.filterNative}
          aria-label={t('targetedMarketing.severity')}
          value={severity}
          onChange={(event) => onSeverityChange(event.target.value)}
        >
          <option value="">{t('targetedMarketing.allSeverities')}</option>
          {severities.map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
        <select
          className={styles.filterNative}
          aria-label={t('targetedMarketing.runStatus')}
          value={runStatus}
          onChange={(event) => onRunStatusChange(event.target.value)}
        >
          <option value="">{t('targetedMarketing.allRunStatuses')}</option>
          {RUN_STATUSES.map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
      </div>
      {issues.length === 0 ? (
        <EmptyList text={t('targetedMarketing.emptyRunIssues')} />
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('targetedMarketing.title')}</th>
                <th>{t('targetedMarketing.severity')}</th>
                <th>{t('targetedMarketing.companyColumn')}</th>
                <th>{t('targetedMarketing.country')}</th>
                <th>{t('targetedMarketing.agents')}</th>
                <th>{t('targetedMarketing.target')}</th>
                <th>{t('targetedMarketing.tickTime')}</th>
                <th>{t('targetedMarketing.status')}</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((card) => (
                <tr key={card.finding.publicId}>
                  <td>
                    <button
                      type="button"
                      className={styles.nameButton}
                      onClick={() => onOpenIssue(card)}
                    >
                      {card.finding.title}
                    </button>
                  </td>
                  <td className={severityClass(card.finding.severity)}>
                    {card.finding.severity}
                  </td>
                  <td>{card.companyName}</td>
                  <td>{card.country || t('targetedMarketing.none')}</td>
                  <td>{card.agentLabel}</td>
                  <td>{card.targetName}</td>
                  <td>{formatIsoDateTime(card.run.scheduledAt)}</td>
                  <td>
                    <span className={`${styles.statusPill} ${statusClass(card.run.status)}`}>
                      {card.run.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

export default WatchTowerRunAnalysis;
